export interface Bonus {
  id: string
  title: string
  subtitle: string
  intro: string[]
  chain?: string
  bulletsLabel: string
  bullets: string[]
  extraList?: { label: string; items: string[] }
  visualValue: string
  closing: string
}

export const bonuses: Bonus[] = [
  {
    id: '01',
    title: '100 Common Olympic Weightlifting Errors & Coach Corrections',
    subtitle:
      'A Visual Troubleshooting Guide for Identifying Technical Problems, Understanding Their Causes & Applying the Right Coaching Fix',
    intro: [
      'Instead of simply seeing that a lift “looks wrong,” use a structured troubleshooting system:',
    ],
    chain: 'See → Identify → Understand → Cue → Drill → Recheck',
    bulletsLabel: 'Covers:',
    bullets: [
      'Setup & starting position',
      'First pull',
      'Transition',
      'Second pull & extension',
      'Bar path',
      'Turnover',
      'Receiving positions',
      'Overhead stability',
      'Snatch',
      'Clean',
      'Front rack',
      'Jerk dip & drive',
      'Jerk footwork',
      'Balance',
      'Timing',
      'Mobility-related restrictions',
      'Strength-related limitations',
    ],
    visualValue:
      'Technical examples, error references, correction cues and structured troubleshooting pages help you identify technical problems faster.',
    closing: 'See the error. Understand the cause. Apply the fix.',
  },
  {
    id: '02',
    title: 'Olympic Weightlifting Warm-Up & Mobility Library',
    subtitle: 'Ready-to-Use Visual Warm-Ups, Mobility Sequences & Movement Preparation',
    intro: [
      'No more random stretching before Weightlifting.',
      'Use organized preparation based on the session and positions your athlete needs.',
    ],
    bulletsLabel: 'Includes:',
    bullets: [
      'General Weightlifting warm-ups',
      'Snatch preparation',
      'Clean preparation',
      'Jerk preparation',
      'Squat preparation',
      'Pulling preparation',
      'Overhead preparation',
      'Front-rack preparation',
      'Ankle mobility',
      'Hip mobility',
      'Thoracic mobility',
      'Shoulder mobility',
      'Wrist preparation',
      '5-minute options',
      '10-minute options',
      'Full preparation sequences',
    ],
    visualValue:
      'Step-by-step movement sequences make it easier to see what comes next and why each drill is being used.',
    closing: 'Prepare with purpose. Move better. Lift better.',
  },
  {
    id: '03',
    title: 'Weightlifting Coaching Cues & Technical Checklist Handbook',
    subtitle: 'A Visual Quick-Reference System for Snatch, Clean, Jerk, Positions, Bar Path & Timing',
    intro: [
      'Good coaching is not about giving an athlete ten corrections at once.',
      'This visual handbook helps you identify the main technical issue and select a short, clear cue.',
    ],
    bulletsLabel: 'Includes:',
    bullets: [
      'Setup cues',
      'First-pull cues',
      'Transition cues',
      'Extension cues',
      'Bar-path checkpoints',
      'Turnover cues',
      'Snatch receiving position',
      'Clean receiving position',
      'Front rack',
      'Overhead stability',
      'Jerk dip and drive',
      'Split Jerk footwork',
      'Recovery',
      'Technical checklists',
    ],
    visualValue:
      'Quick-reference cards and technical checklists are designed to be scanned rapidly during real coaching sessions.',
    closing: 'Observe. Identify. Cue. Reassess.',
  },
  {
    id: '04',
    title: 'Olympic Weightlifting Athlete Testing & Progress Tracking System',
    subtitle: 'A Visual Coaching Framework for Assessment, Performance Records & Athlete Progress',
    intro: ['Athlete development becomes easier to manage when progress is visible.'],
    bulletsLabel: 'Includes:',
    bullets: [
      'Athlete profile',
      'Training background',
      'Technical assessment',
      'Snatch evaluation',
      'Clean & Jerk evaluation',
      'Strength markers',
      'Mobility observations',
      'Position quality',
      'Balance',
      'Timing',
      'Receiving positions',
      'Training records',
      'Weekly progress review',
      'Monthly progress review',
      'Next-priority planning',
    ],
    visualValue:
      'Assessment sheets, scoring systems and progress trackers allow you to see athlete development at a glance.',
    closing: 'Assess. Track. Review. Improve.',
  },
  {
    id: '05',
    title: '50 Olympic Weightlifting Drills for Technique Correction',
    subtitle: 'A Visual Drill Library for Snatch, Clean, Jerk, Bar Path, Timing & Receiving Positions',
    intro: [
      'Exactly 50 technical drills organized around the technical problem they are designed to correct.',
    ],
    bulletsLabel: 'Includes drills for:',
    bullets: [
      'Starting position',
      'First pull',
      'Staying over the bar',
      'Bar path',
      'Balance',
      'Extension',
      'Timing',
      'Turnover',
      'Snatch receiving position',
      'Overhead stability',
      'Clean turnover',
      'Front rack',
      'Clean receiving position',
      'Jerk dip',
      'Jerk drive',
      'Split footwork',
      'Recovery',
    ],
    extraList: {
      label: 'Each drill includes:',
      items: [
        'Purpose',
        'Setup',
        'Execution',
        'Sets / Reps',
        'Coaching Cue',
        'Common Error',
        'Correction',
        'When to Use It',
      ],
    },
    visualValue:
      'Instead of simply reading the name of a drill, the system helps make the setup, movement and technical objective easier to understand visually.',
    closing: 'See the problem. Find the drill. Coach the correction.',
  },
]
